import type { QueryFunctionContext } from "@tanstack/react-query";
import type { CustomerOrderSummary } from "@shared/customer-order-summary";
import { apiRequest, CACHE_TIMES } from "@/lib/queryClient";

/**
 * Customer data is deliberately partitioned by the customer id, never by the
 * bearer token. Tokens rotate during refresh and must not become cache keys.
 */
export const customerOrderSummaryQueryKey = (customerId: string) =>
  ["/api/orders/summary", customerId] as const;

export const customerOrdersQueryKey = (customerId: string) =>
  ["/api/orders", customerId] as const;

export const customerOrderDetailQueryKey = (customerId: string, orderId: string) =>
  ["/api/orders", customerId, orderId] as const;

export function getCustomerIdFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    // JWT claims are used only as an untrusted cache partition hint. The API
    // still authenticates every request with the bearer token.
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return null;
    const normalizedPayload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(
      atob(normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4)),
    );
    return typeof payload?.customerId === "string" && payload.customerId.length > 0
      ? payload.customerId
      : null;
  } catch {
    return null;
  }
}

function assertCustomerIdentity(expectedCustomerId: string): void {
  if (
    typeof localStorage === "undefined" ||
    getCustomerIdFromToken(localStorage.getItem("customer_token")) !== expectedCustomerId
  ) {
    throw new Error("La sessione cliente è cambiata. Ricarica la pagina.");
  }
}

async function getCustomerJson<T>(
  expectedCustomerId: string,
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  assertCustomerIdentity(expectedCustomerId);
  const response = await apiRequest("GET", url, undefined, { signal });
  // A logout/login or cross-tab switch may happen while fetch is pending.
  assertCustomerIdentity(expectedCustomerId);
  const data = await response.json() as T;
  assertCustomerIdentity(expectedCustomerId);
  return data;
}

export function customerOrderSummaryQuery(customerId: string) {
  return {
    queryKey: customerOrderSummaryQueryKey(customerId),
    // Do not derive this URL from queryKey: the identity segment is only a
    // cache partition and is not part of the API route.
    queryFn: ({ signal }: QueryFunctionContext) =>
      getCustomerJson<CustomerOrderSummary>(customerId, "/api/orders/summary", signal),
    staleTime: CACHE_TIMES.user,
    // The dashboard is also prefetched from the header. Fresh prefetched data
    // must be used on mount instead of being fetched a second time.
    refetchOnMount: true,
  };
}

export function customerOrdersQuery<T = unknown>(customerId: string) {
  return {
    queryKey: customerOrdersQueryKey(customerId),
    queryFn: ({ signal }: QueryFunctionContext) =>
      getCustomerJson<T[]>(customerId, "/api/orders", signal),
    staleTime: CACHE_TIMES.user,
    refetchOnMount: true,
  };
}

export function customerOrderDetailQuery<T = unknown>(customerId: string, orderId: string) {
  return {
    queryKey: customerOrderDetailQueryKey(customerId, orderId),
    queryFn: ({ signal }: QueryFunctionContext) =>
      getCustomerJson<T>(customerId, `/api/orders/${encodeURIComponent(orderId)}`, signal),
    staleTime: CACHE_TIMES.user,
    refetchOnMount: true,
  };
}

/**
 * The customer id is read from the token claims only as a cache hint.
 * Requiring a customer token prevents anonymous private prefetches; claims
 * are never treated as server authorization.
 */
export function getStoredCustomerIdentity(): string | null {
  if (typeof localStorage === "undefined") return null;
  return getCustomerIdFromToken(localStorage.getItem("customer_token"));
}
